import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars, scoped, declare, override } from '#utils/styles'

export const locals = scoped('form-boolean', [
	'color',
	'background_color',
	'border_color',
	'state_opacity'
] as const)

export const localsBoolean = scoped('form-boolean', [
	'background_color_control',
	'border_color_control'
] as const)

export const boolean = css`
	:host {
		${declare(localsBoolean, {
			background_color_control: tokens.color_background_secondary,
			border_color_control: tokens.color_border_tertiary
		})};
	}

	/* Input — 48px transparent click area, centered over the box */
	.boolean {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: ${vars(tokens.size_control_sm)};
		height: ${vars(tokens.size_control_sm)};
		appearance: none;
		-webkit-tap-highlight-color: transparent;
		border: 0;
		padding: 0;
		margin: 0;
		cursor: pointer;
		background: transparent;
		border-radius: ${vars(tokens.border_radius_round)};
		z-index: 1;

		&:focus {
			outline: 0;
		}
	}

	/* Visual box — 24px indicator rendered via pseudo */
	.control::before {
		content: '';
		display: block;
		box-sizing: border-box;
		width: ${vars(tokens.spacing_xl)};
		height: ${vars(tokens.spacing_xl)};
		border: ${vars(tokens.border_width_emphasis)} solid
			${vars(localsBoolean.border_color_control)};
		border-radius: ${vars(tokens.border_radius_xs)};
		background-color: ${vars(localsBoolean.background_color_control)};
		transition: 100ms border-color var(--ease-in);
	}

	/* Checked — filled box */
	.control.checked::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_background_selected
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_selected
		)};
	}

	/* Hover on unchecked — border highlight */
	.control:has(:hover)::before {
		${override(localsBoolean.border_color_control, tokens.color_border_hover)};
		transition-timing-function: var(--ease-out);
	}

	/* Focus on unchecked — border highlight */
	.control:has(:focus-visible)::before {
		${override(localsBoolean.border_color_control, tokens.color_border_focus)};
		transition-timing-function: var(--ease-out);
	}

	/* Pressed on unchecked */
	.control:has(:active)::before {
		${override(localsBoolean.border_color_control, tokens.color_border_hover)};
		transition-timing-function: var(--ease-out);
	}

	/* Hover on checked — maintain filled state */
	.control.checked:has(:hover)::before,
	.control:has(:indeterminate:hover)::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_background_selected
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_selected
		)};
	}

	/* Focus on checked — maintain filled state */
	.control.checked:has(:focus-visible)::before,
	.control:has(:indeterminate:focus-visible)::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_background_selected
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_selected
		)};
	}

	/* Pressed on checked — maintain filled state */
	.control.checked:has(:active)::before,
	.control:has(:indeterminate:active)::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_background_selected
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_selected
		)};
	}

	/* Validation message — error border on unchecked control only */
	.control.has-message:not(.checked)::before {
		${override(localsBoolean.border_color_control, tokens.color_border_error)};
	}

	/* Checkbox icon indicator (indeterminate — uses lfds-icon) */
	.check-icon {
		position: absolute;
		width: ${vars(tokens.spacing_xl)};
		height: ${vars(tokens.spacing_xl)};
		display: flex;
		align-items: center;
		justify-content: center;
		pointer-events: none;
		opacity: 0;
		color: ${vars(tokens.color_icon_on_button_primary)};
		transition: 100ms opacity var(--ease-in);
	}

	.control:has(:indeterminate) .check-icon {
		opacity: 1;
		transition-timing-function: var(--ease-out);
	}

	/* Checkbox stroke-draw indicator (checked) */
	.check-stroke {
		position: absolute;
		width: ${vars(tokens.spacing_xl)};
		height: ${vars(tokens.spacing_xl)};
		pointer-events: none;
		color: ${vars(tokens.color_icon_on_button_primary)};

		polyline {
			stroke-dasharray: 20;
			stroke-dashoffset: 20;
		}
	}

	.control.checked .check-stroke polyline {
		animation: lfds-check-draw 350ms var(--ease-out) forwards;
	}

	@keyframes lfds-check-draw {
		from {
			stroke-dashoffset: 20;
		}
		to {
			stroke-dashoffset: 0;
		}
	}

	/* Radio — circular box */
	.control:has(.radio)::before {
		border-radius: 50%;
	}

	/* Radio indeterminate — reset to default (CSS :indeterminate matches unchecked radios) */
	.control:has(.radio:indeterminate)::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_border_tertiary
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_secondary
		)};
	}

	/* Radio checked — keep background white, only border changes */
	.control.checked:has(.radio)::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_background_selected
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_secondary
		)};
	}

	/* Radio hover on unchecked — border highlight, keep white bg */
	.control:has(.radio:indeterminate:hover)::before {
		${override(localsBoolean.border_color_control, tokens.color_border_hover)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_secondary
		)};
	}

	/* Radio focus on unchecked — border highlight, keep white bg */
	.control:has(.radio:indeterminate:focus-visible)::before {
		${override(localsBoolean.border_color_control, tokens.color_border_focus)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_secondary
		)};
	}

	/* Radio pressed on unchecked */
	.control:has(.radio:indeterminate:active)::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_border_pressed
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_secondary
		)};
	}

	/* Radio hover on checked — maintain white background */
	.control.checked:has(.radio:hover)::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_background_selected
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_secondary
		)};
	}

	/* Radio focus on checked — maintain white background */
	.control.checked:has(.radio:focus-visible)::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_background_selected
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_secondary
		)};
	}

	/* Radio pressed on checked — maintain white background */
	.control.checked:has(.radio:active)::before {
		${override(
			localsBoolean.border_color_control,
			tokens.color_background_selected
		)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_secondary
		)};
	}

	/* Radio invalid state — red border */
	.control.invalid:has(.radio)::before {
		${override(localsBoolean.border_color_control, tokens.color_border_error)};
		${override(
			localsBoolean.background_color_control,
			tokens.color_background_secondary
		)};
	}

	/* Radio dot indicator */
	.radio-dot {
		position: absolute;
		width: ${vars(tokens.spacing_xl)};
		height: ${vars(tokens.spacing_xl)};
		display: flex;
		align-items: center;
		justify-content: center;
		pointer-events: none;

		&::before {
			content: '';
			opacity: 0;
			background-color: ${vars(tokens.color_background_selected)};
			border-radius: 50%;
			width: ${vars(tokens.spacing_md)};
			height: ${vars(tokens.spacing_md)};
			transition: 100ms var(--ease-in);
			transition-property: opacity, transform;
			transform: scale(0);
			transform-origin: center center;
		}
	}

	.control.checked:has(.radio) .radio-dot::before {
		opacity: 1;
		transition-timing-function: var(--ease-out);
		transform: scale(1);
	}

	/* Reduced motion */
	@media (prefers-reduced-motion: reduce) {
		.control::before,
		.radio-dot::before {
			transition-duration: 0.01ms !important;
		}

		.check-stroke polyline {
			animation-duration: 0.01ms !important;
		}
	}
`

export default css`
	:host {
		${declare(locals, {
			color: tokens.color_text_primary,
			background_color: tokens.color_state_layer_on_light,
			state_opacity: '0'
		})};

		display: inline-flex;
		flex-direction: column;
		gap: ${vars(tokens.spacing_xs)};

		&:has(.boolean:disabled) {
			${override(locals.color, tokens.color_text_disabled)};
			cursor: default;
		}
	}

	.wrapper {
		box-sizing: border-box;
		display: inline-flex;
		align-items: flex-start;
		gap: ${vars(tokens.spacing_lg)};
		min-height: ${vars(tokens.size_control_sm)};
		position: relative;
		z-index: 0;
		vertical-align: middle;
		cursor: pointer;
		-webkit-tap-highlight-color: transparent;

		&:has(.boolean:disabled) {
			cursor: default;
		}
	}

	.label {
		cursor: pointer;
		padding-block: calc(
			(
					${vars(tokens.size_control_sm)} -
						${vars(tokens.line_height_body_default)}
				) /
				2
		);
	}

	/* Control sized to the visual box — state layer and input do not affect layout */
	.control {
		position: relative;
		align-self: flex-start;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		min-height: ${vars(tokens.size_control_sm)};
		cursor: pointer;
	}

	/*
	 * State-layer — absolutely positioned behind the box, centered.
	 * Controlled by --lfds--form-boolean-state-opacity.
	 * Can be set externally (e.g. list-row hover) or internally (hover/focus).
	 */
	.control::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: ${vars(tokens.size_control_sm)};
		height: ${vars(tokens.size_control_sm)};
		border-radius: ${vars(tokens.border_radius_round)};
		background-color: color-mix(
			in srgb,
			${vars(locals.background_color)}
				calc(${vars(locals.state_opacity)} * 100%),
			transparent
		);
		transition: 100ms background-color var(--ease-in);
		z-index: -1;
		pointer-events: none;
	}

	/* Hover state */
	.control:hover,
	.wrapper:hover .control {
		${override(locals.state_opacity, tokens.effect_opacity_state_hover)};
	}

	.control:hover::after,
	.wrapper:hover .control::after {
		transition-timing-function: var(--ease-out);
	}

	/* Focus state */
	.control:has(input:focus-visible),
	.wrapper:has(input:focus-visible) .control {
		${override(locals.state_opacity, tokens.effect_opacity_state_focused)};
	}

	.control:has(input:focus-visible)::after,
	.wrapper:has(input:focus-visible) .control::after {
		transition-timing-function: var(--ease-out);
	}

	/* Pressed state */
	.control:active,
	.wrapper:active .control {
		${override(locals.state_opacity, tokens.effect_opacity_state_pressed)};
	}

	/* Focus outline ring — mixin class + CSS pseudo, circular via input border-radius */
	.wrapper.focus-visible .boolean:focus-visible {
		box-shadow:
			0 0 0 ${vars(tokens.border_width_focus)} white,
			0 0 0 calc(${vars(tokens.border_width_focus)} * 2)
				${vars(tokens.color_border_focus_outline)};
	}

	.wrapper *:not(::slotted(*)) {
		box-sizing: inherit;
		margin: 0;
		padding: 0;
	}

	.validation {
		display: flex;
		align-items: flex-start;
		cursor: default;
		gap: ${vars(tokens.spacing_xs)};
		color: ${vars(tokens.color_text_error)};
		line-height: calc(18 / 14);
		font-size: 0.875rem;

		.icon {
			margin: -0.2rem 0 0 -0.15rem;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.control::after {
			transition-duration: 0.01ms !important;
		}
	}
`
